<script setup>
import { computed } from 'vue';
import { longDate, shortDate } from '../../helpers/usDate';
import IconTriangleWarning from '../icons/IconTriangleWarning.vue';
import XlAvatar from '../xl/XlAvatar.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import XlTable from '../xl/XlTable.vue';
import XlTag from '../xl/XlTag.vue';
import XlTooltip from '../xl/XlTooltip.vue';
import XlTruncate from '../xl/XlTruncate.vue';
import { TERM_TIPS } from '../../helpers/termTips';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  selectedIds: { type: Array, default: () => [] },
  // Only an owner or hub admin sees who a policyholder belongs to; a broker is
  // already looking at nothing but their own book.
  showBroker: { type: Boolean, default: false },
  brokerNameById: { type: Function, default: () => '' },
  // '' when the list is in its default order, else 'asc' | 'desc'.
  renewalSort: { type: String, default: '' },
  /**
   * The rows are on their way. The table draws its own head and, in its own
   * columns, rows of placeholders at a real row's height — every bar the same
   * width down a column — so nothing moves when the rows land (Policyholders:
   * the skeleton was ragged bars of no particular column, XN-4902 AC4).
   */
  loading: { type: Boolean, default: false },
  loadingRows: { type: Number, default: 8 },
});

const emit = defineEmits(['open', 'message', 'toggle', 'toggle-all', 'sort']);

const allSelected = computed(
  () => props.rows.length > 0 && props.rows.every((row) => props.selectedIds.includes(row.id))
);

const nameOf = (row) => row.fullname || `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || row.email;

const registeredOn = (row) => shortDate(row.created_at) || '—';

/**
 * Renewal is per policy, so this is the nearest one — never a per-person date.
 * A lapsed renewal reports as lapsed rather than being floored at zero: it is
 * the row most worth chasing.
 */
const renewalTone = (row) => {
  const days = row.next_renewal?.days_remaining;
  if (days === null || days === undefined) return '';
  if (days <= 30) return 'error';
  if (days <= 90) return 'warning';
  return 'success';
};

const renewalLabel = (row) => {
  const renewal = row.next_renewal;
  if (!renewal?.renewal_date) return '';

  const on = longDate(renewal.renewal_date);
  const type = renewal.type ? `${renewal.type[0].toUpperCase()}${renewal.type.slice(1)} ` : '';

  return `${type}${on}`;
};

const otherPolicies = (row) => Math.max(0, (row.policy_count ?? 0) - 1);
</script>

<template>
  <!-- Busy while the rows are on the way; each placeholder hides itself (XlSkeleton),
       never a row or a cell, which aria-hidden must not be put on. -->
  <XlTable :aria-busy="props.loading ? 'true' : undefined">
    <template #head>
      <th class="policyholder-table__tick">
        <input
          type="checkbox"
          aria-label="Select every policyholder on this page"
          :checked="allSelected"
          :disabled="props.loading"
          @change="emit('toggle-all', !allSelected)"
        />
      </th>
      <th>Name</th>
      <th><XlTooltip :text="TERM_TIPS['Policy lines']" placement="bottom" underline>Policy lines</XlTooltip></th>
      <th>
        <!--
          The heading does two jobs, so the explanation and the control are
          separate targets: the word carries the tooltip, the button sorts.
        -->
        <XlTooltip :text="TERM_TIPS['Next renewal']" placement="bottom" underline>Next renewal</XlTooltip>
        <button
          type="button"
          class="policyholder-table__sort"
          :class="{ 'policyholder-table__sort--on': props.renewalSort }"
          :aria-label="`Sort by next renewal, ${props.renewalSort === 'asc' ? 'soonest first' : 'furthest first'}`"
          :aria-pressed="Boolean(props.renewalSort)"
          data-testid="sort-next-renewal"
          @click="emit('sort', props.renewalSort === 'asc' ? 'desc' : 'asc')"
        >{{ props.renewalSort === 'desc' ? '↓' : '↑' }}</button>
      </th>
      <th>Registered</th>
      <th><XlTooltip :text="TERM_TIPS.Resources" placement="bottom" underline>Resources</XlTooltip></th>
      <th v-if="props.showBroker"><XlTooltip :text="TERM_TIPS.Broker" placement="bottom" underline>Broker</XlTooltip></th>
      <th class="policyholder-table__actions-head">Actions</th>
    </template>

    <template v-if="props.loading">
      <tr v-for="n in props.loadingRows" :key="`loading-${n}`" data-testid="policyholder-row-loading">
        <td><XlSkeleton class="policyholder-table__sk" width="16px" height="16px" radius="4px" /></td>
        <td>
          <div class="policyholder-table__person">
            <XlSkeleton class="policyholder-table__sk" variant="circular" width="38px" height="38px" />
            <span class="policyholder-table__identity policyholder-table__identity--loading">
              <XlSkeleton class="policyholder-table__sk" width="132px" height="0.85em" radius="6px" />
              <XlSkeleton class="policyholder-table__sk" width="196px" height="0.75em" radius="6px" />
            </span>
          </div>
        </td>
        <td><XlSkeleton class="policyholder-table__sk" width="48px" height="24px" radius="99px" /></td>
        <td><XlSkeleton class="policyholder-table__sk" width="150px" height="24px" radius="99px" /></td>
        <td><XlSkeleton class="policyholder-table__sk" width="80px" height="0.85em" radius="6px" /></td>
        <td><XlSkeleton class="policyholder-table__sk" width="38px" height="24px" radius="99px" /></td>
        <td v-if="props.showBroker"><XlSkeleton class="policyholder-table__sk" width="96px" height="0.85em" radius="6px" /></td>
        <td>
          <div class="policyholder-table__actions">
            <XlSkeleton class="policyholder-table__sk" width="32px" height="32px" radius="11px" />
            <XlSkeleton class="policyholder-table__sk" width="32px" height="32px" radius="11px" />
          </div>
        </td>
      </tr>
    </template>

    <tr
      v-for="row in (props.loading ? [] : props.rows)"
      :key="row.id"
      class="is-clickable"
      :class="{ 'is-selected': props.selectedIds.includes(row.id) }"
      data-testid="policyholder-row"
      @click="emit('open', row)"
    >
      <td @click.stop>
        <input
          type="checkbox"
          :aria-label="`Select ${nameOf(row)}`"
          :checked="props.selectedIds.includes(row.id)"
          @change="emit('toggle', row)"
        />
      </td>

      <td>
        <div class="policyholder-table__person">
          <XlAvatar :name="nameOf(row)" :src="row.profile_image || ''" :color="row.avatar_color || ''" />
          <span class="policyholder-table__identity">
            <b>
              <XlTruncate :text="nameOf(row) || ''" />
            </b>
            <span class="policyholder-table__email">
              <XlTruncate :text="row.email ?? ''" />
            </span>
          </span>
        </div>
      </td>

      <td>
        <div v-if="(row.policy_lines ?? []).length" class="policyholder-table__lines">
          <XlTag v-for="line in row.policy_lines" :key="line.id ?? line.name" tone="brand">{{ line.name }}</XlTag>
        </div>
        <span v-else class="policyholder-table__none">— none</span>
      </td>

      <td>
        <template v-if="row.next_renewal?.renewal_date">
          <XlTag :tone="renewalTone(row)">{{ renewalLabel(row) }}</XlTag>
          <span v-if="otherPolicies(row)" class="policyholder-table__more">+{{ otherPolicies(row) }} more</span>
        </template>
        <XlTag v-else-if="row.policy_count" tone="error" class="policyholder-table__nodate"><IconTriangleWarning color="currentColor" /> no date</XlTag>
        <span v-else class="policyholder-table__none">— no policy</span>
      </td>

      <td class="policyholder-table__muted">{{ registeredOn(row) }}</td>

      <td>
        <XlTag v-if="row.resource_count">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" />
          </svg>
          {{ row.resource_count }}
        </XlTag>
        <span v-else class="policyholder-table__none">0</span>
      </td>

      <td v-if="props.showBroker">
        <span v-if="row.broker_id" class="policyholder-table__broker">{{ props.brokerNameById(row.broker_id) }}</span>
        <XlTag v-else tone="error">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 4 2.5 20h19z" /><path d="M12 10v4M12 17h.01" />
          </svg>
          nobody
        </XlTag>
      </td>

      <td @click.stop>
        <div class="policyholder-table__actions">
          <XlTooltip text="Send a message" control>
            <button type="button" :aria-label="`Message ${nameOf(row)}`" @click="emit('message', row)">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3.5 7 8.5 6 8.5-6" />
              </svg>
            </button>
          </XlTooltip>
          <XlTooltip text="Open" control>
            <button type="button" :aria-label="`Open ${nameOf(row)}`" @click="emit('open', row)">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </XlTooltip>
        </div>
      </td>
    </tr>

    <tr v-if="!props.loading && !props.rows.length && $slots.empty">
      <td :colspan="props.showBroker ? 8 : 7"><div class="policyholder-table__empty"><slot name="empty" /></div></td>
    </tr>
  </XlTable>
</template>

<style scoped>
.policyholder-table__tick { width: 38px; }
.policyholder-table__actions-head { width: 96px; }

.policyholder-table__sort {
  border: 0;
  padding: 0 0 0 4px;
  background: none;
  font: inherit;
  color: var(--xl-color-outline);
  cursor: pointer;
}

.policyholder-table__sort--on { color: var(--xl-color-primary); }

.policyholder-table__person { display: flex; align-items: center; gap: 11px; }
.policyholder-table__identity { min-width: 0; }
.policyholder-table__identity--loading { display: flex; flex-direction: column; gap: 7px; }
.policyholder-table__sk { width: auto; flex: 0 0 auto; vertical-align: middle; }
.policyholder-table__identity b {
  display: grid;
  grid-template-columns: minmax(0, max-content);
}

.policyholder-table__email {
  display: grid;
  grid-template-columns: minmax(0, max-content);
  font-size: var(--xl-type-body-small-size);
  color: var(--xl-color-outline);
}

.policyholder-table__lines { display: flex; gap: 4px; flex-wrap: wrap; }

.policyholder-table__none { color: var(--xl-color-outline-variant); white-space: nowrap; }
.policyholder-table__nodate :deep(svg) { width: 11px; height: 11px; vertical-align: -1px; margin-right: 3px; }
.policyholder-table__muted { color: var(--xl-color-outline); white-space: nowrap; }
.policyholder-table__broker { color: var(--xl-color-outline); }

.policyholder-table__more {
  margin-left: 6px;
  font-size: var(--xl-type-body-small-size);
  color: var(--xl-color-outline);
  white-space: nowrap;
}

/* The empty row spans the table's full scroll width; held to the visible part
   of the scroller, or on a phone its "Clear them" sits off-screen. */
.xl-table__scroll { container-type: inline-size; }

@container (max-width: 1000px) {
  .xl-table__scroll :deep(.xl-table th),
  .xl-table__scroll :deep(.xl-table td) {
    padding-inline: 8px;
  }
}

.policyholder-table__empty {
  position: sticky;
  left: 16px;
  width: calc(100cqw - 32px);
  padding: 21px 0;
  text-align: center;
  color: var(--xl-color-outline);
}

.policyholder-table__actions { display: flex; gap: 5px; }

.policyholder-table__actions button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border: 1px solid var(--xl-color-border);
  border-radius: 11px;
  background: transparent;
  color: var(--xl-color-secondary-text);
  cursor: pointer;
  line-height: 1;
}

.policyholder-table__actions button:hover {
  background: #fcfafd;
  color: var(--xl-color-primary);
}
</style>
